/**
 * AxelLiveTrigger — AXEL LIVE TRIGGER panel.
 *
 * A current-price-sensitive signal mode that sits alongside MT5 Setup inside
 * the CFD Desk. Calls POST /api/axel/live-trigger which anchors the trade idea
 * to the EXACT live price + timestamp at request time. Renders premium cards
 * (no raw JSON) plus a clean copy/export block for manual MT5 use.
 *
 * MT5 Setup is untouched — this is additive.
 */
import { useState, useEffect } from "react";
import { runAxelJob } from "./axelJob";
import { useIntelHubOptional } from "../context/IntelHubContext";

// Signal-type → color/icon
const SIG_STYLE = {
  "MARKET BUY":  { c: "#00d68f", icon: "🟢", label: "MARKET BUY" },
  "MARKET SELL": { c: "#ff4757", icon: "🔴", label: "MARKET SELL" },
  "BUY STOP":    { c: "#00d68f", icon: "⤴", label: "BUY STOP" },
  "SELL STOP":   { c: "#ff4757", icon: "⤵", label: "SELL STOP" },
  "BUY LIMIT":   { c: "#34D399", icon: "⏳", label: "BUY LIMIT" },
  "SELL LIMIT":  { c: "#fb7185", icon: "⏳", label: "SELL LIMIT" },
  "WAIT":        { c: "#F5A623", icon: "✋", label: "WAIT / NO TRADE" },
  "NO TRADE":    { c: "#F5A623", icon: "✋", label: "WAIT / NO TRADE" },
};

const RISK_COLOR = { High: "#ff4757", Medium: "#F5A623", "Low-Medium": "#fbbf24", Low: "#00d68f" };

// Live Trigger is live for these now; oil pairs route through MT5 Setup until added.
const LT_SUPPORTED = ["XAUUSD", "BTCUSD"];

// Assets wired to the FlexxAxelBridge MT5 execution queue.
const MT5_BRIDGE_ASSETS = ["XAUUSD", "BTCUSD"];

// Live Trigger already decides the exact order type — map it straight to the
// MT5 bridge order_type + direction (no re-classification needed).
const LT_ORDER_MAP = {
  "MARKET BUY":  { order_type: "MARKET",     direction: "BUY",  market: true },
  "MARKET SELL": { order_type: "MARKET",     direction: "SELL", market: true },
  "BUY STOP":    { order_type: "BUY_STOP",   direction: "BUY"  },
  "SELL STOP":   { order_type: "SELL_STOP",  direction: "SELL" },
  "BUY LIMIT":   { order_type: "BUY_LIMIT",  direction: "BUY"  },
  "SELL LIMIT":  { order_type: "SELL_LIMIT", direction: "SELL" },
};

async function mt5Api(path, opts = {}) {
  const token = localStorage.getItem("flexx_access_token");
  const headers = { "Content-Type": "application/json" };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api${path}`, { credentials: "include", headers, ...opts });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(typeof d.detail === "string" ? d.detail : `Request failed (${r.status})`);
  return d;
}

function fmtNum(n) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
  const v = Number(n);
  return v >= 1000 ? v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                   : v.toLocaleString(undefined, { maximumFractionDigits: 5 });
}

function Spinner() {
  return <span style={{ display: "inline-block", width: 14, height: 14, border: "2px solid #2a3040", borderTopColor: "#F5A623", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />;
}

function InfoCard({ title, color, children, testid }) {
  return (
    <div data-testid={testid} style={{ background: "var(--surf)", border: "1px solid var(--border)", borderLeft: `3px solid ${color}`, borderRadius: 8, padding: "11px 13px" }}>
      <div style={{ fontFamily: "var(--hd)", fontSize: ".62rem", letterSpacing: ".12em", textTransform: "uppercase", color, marginBottom: 5 }}>{title}</div>
      <div style={{ fontSize: ".8rem", color: "var(--text)", fontFamily: "var(--bd)", lineHeight: 1.6 }}>{children}</div>
    </div>
  );
}

async function fetchLiveTrigger(symbol, context) {
  // Async-job pattern: start the job, then poll a fast status endpoint until
  // done. Keeps each HTTP request short so a slow production origin can't 504.
  return runAxelJob(
    "/api/axel/live-trigger",
    { symbol, context },
  );
}

// ── EXECUTE LIVE TRIGGER — routes a Live Trigger signal to the FlexxAxelBridge ──
// Mirrors the MT5 Setup "EXECUTE MT5" gate, but the order type is already
// decided by the Live Trigger (signal_type), so no re-classification UI.
function LiveTriggerExecution({ data, asset }) {
  const map = LT_ORDER_MAP[data.signal_type];
  const isMarket = !!(map && map.market);
  const entry = isMarket ? null : Number(data.entry?.price);
  const [val, setVal] = useState(null);
  const [busy, setBusy] = useState(false);
  const [execResult, setExecResult] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!map) { setVal({ valid: false, message: "Unsupported signal type for execution." }); return; }
      try {
        const v = await mt5Api("/mt5/validate", {
          method: "POST",
          body: JSON.stringify({
            symbol: asset, direction: map.direction, entry, order_type: map.order_type,
            stop_loss: data.stop_loss != null ? Number(data.stop_loss) : null,
            tp1: data.targets?.tp1 != null ? Number(data.targets.tp1) : null,
          }),
        });
        if (alive) setVal(v);
      } catch (e) { if (alive) setVal({ valid: false, message: e.message }); }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asset, data.signal_type, entry]);

  async function execute() {
    if (!map) return;
    setBusy(true); setErr(""); setExecResult(null);
    try {
      const res = await mt5Api("/mt5/execute", {
        method: "POST",
        body: JSON.stringify({
          symbol: asset, direction: map.direction,
          entry, order_type: map.order_type,
          stop_loss: Number(data.stop_loss),
          tp1: Number(data.targets?.tp1),
          tp2: data.targets?.tp2 != null ? Number(data.targets.tp2) : null,
          tp3: data.targets?.tp3 != null ? Number(data.targets.tp3) : null,
          confidence: data.confidence != null ? parseInt(data.confidence, 10) : null,
          setup_type: data.setup_type || "",
          source: "live_trigger",
        }),
      });
      setExecResult(res);
    } catch (e) { setErr(e.message); }
    setBusy(false);
  }

  if (!map) return null;
  const canExecute = val && val.pass;
  const msgColor = !val ? "var(--muted)" : val.valid ? "#00d68f" : "#ff4757";

  return (
    <div data-testid="lt-exec-preview" style={{ padding: "14px 15px", background: "#04070f", border: "1px solid #1c2c22", borderRadius: 10, display: "grid", gap: 12, marginBottom: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <div style={{ fontFamily: "var(--hd)", fontSize: ".82rem", fontWeight: 800, color: "#00d68f", letterSpacing: ".08em" }}>⚡ MT5 EXECUTION — FLEXX BRIDGE</div>
        <span data-testid="lt-exec-ordertype-badge" style={{ fontFamily: "var(--mono)", fontSize: ".72rem", fontWeight: 700, color: "#00d68f", border: "1px solid #00d68f", borderRadius: 5, padding: "3px 9px" }}>
          {map.order_type} · {isMarket ? "IMMEDIATE" : "PENDING"}
        </span>
      </div>

      <div data-testid="lt-exec-validation" style={{ fontFamily: "var(--mono)", fontSize: ".76rem", color: msgColor, display: "flex", alignItems: "center", gap: 7 }}>
        <span>{!val ? "…" : val.valid ? "✓" : "✕"}</span>
        <span>{val ? val.message : "Checking price rules…"}</span>
        {val && val.current_price ? <span style={{ color: "var(--muted)", fontSize: ".68rem" }}>· live {val.current_price}</span> : null}
      </div>

      {val && (
        <div data-testid="lt-exec-verdict" style={{ display: "grid", gap: 8, padding: "10px 12px", background: "#020608", border: `1px solid ${canExecute ? "#0d3320" : "#3a1520"}`, borderRadius: 8 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
            <span style={{ fontSize: ".62rem", letterSpacing: ".12em", color: "var(--muted)", textTransform: "uppercase" }}>Pre-trade check</span>
            <span data-testid="lt-exec-passblock" style={{ fontFamily: "var(--hd)", fontSize: ".78rem", fontWeight: 800, letterSpacing: ".08em", color: canExecute ? "#00d68f" : "#ff4757", border: `1.5px solid ${canExecute ? "#00d68f" : "#ff4757"}`, borderRadius: 5, padding: "3px 12px" }}>
              {canExecute ? "✓ PASS" : "⛔ BLOCK"}
            </span>
          </div>
          {!canExecute && val.block_reasons && val.block_reasons.length > 0 && (
            <ul data-testid="lt-exec-block-reasons" style={{ margin: 0, paddingLeft: 16, color: "#ff8095", fontSize: ".72rem", fontFamily: "var(--bd)", lineHeight: 1.7 }}>
              {val.block_reasons.map((r) => <li key={r}>{r}</li>)}
            </ul>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(90px, 1fr))", gap: 8, fontFamily: "var(--mono)", fontSize: ".7rem" }}>
            <div><div style={{ color: "var(--muted)", fontSize: ".58rem", letterSpacing: ".08em" }}>RISK MODE</div><div style={{ color: "var(--white)" }}>{(val.risk_mode || "fixed_lot") === "fixed_lot" ? "Fixed lot" : "Risk %"}</div></div>
            <div><div style={{ color: "var(--muted)", fontSize: ".58rem", letterSpacing: ".08em" }}>LOT SIZE</div><div style={{ color: "var(--white)" }}>{val.lot_size != null ? val.lot_size : "auto (risk %)"}</div></div>
            <div><div style={{ color: "var(--muted)", fontSize: ".58rem", letterSpacing: ".08em" }}>STATUS</div><div style={{ color: canExecute ? "#00d68f" : "#ff4757" }}>{canExecute ? (isMarket ? "IMMEDIATE" : "PENDING") : "BLOCKED"}</div></div>
          </div>
        </div>
      )}

      <pre data-testid="lt-exec-payload" style={{ margin: 0, padding: "10px 12px", background: "#020608", border: "1px solid #1c2030", borderRadius: 7, color: "#9fb0c3", fontFamily: "var(--mono)", fontSize: ".72rem", lineHeight: 1.7, overflowX: "auto" }}>
{`SYMBOL: ${asset}
ORDER_TYPE: ${map.order_type}
DIRECTION: ${map.direction}
${isMarket ? "EXECUTION: IMMEDIATE" : `ENTRY_PRICE: ${entry ?? "—"}
EXECUTION: PENDING`}
SL: ${data.stop_loss ?? "—"}
TP1: ${data.targets?.tp1 ?? "—"}${data.targets?.tp2 ? `\nTP2: ${data.targets.tp2}` : ""}${data.targets?.tp3 ? `\nTP3: ${data.targets.tp3}` : ""}`}
      </pre>

      {err && <div data-testid="lt-exec-error" style={{ color: "#ff4757", fontSize: ".76rem" }}>{err}</div>}
      {execResult ? (
        <div data-testid="lt-exec-success" style={{ padding: "10px 12px", background: "#031a0e", border: "1px solid #0d3320", borderRadius: 7, color: "#00d68f", fontSize: ".78rem", fontFamily: "var(--bd)" }}>
          ✓ Queued to your MT5 bridge — <b>{execResult.order_type}</b> · status <b>{execResult.status}</b>. Track it in Profile → MT5 Connection.
        </div>
      ) : (
        <button data-testid="lt-exec-btn" onClick={execute} disabled={!canExecute || busy}
          style={{ width: "100%", padding: "13px", background: canExecute ? "#00d68f" : "transparent", border: `1.5px solid ${canExecute ? "#00d68f" : "#2a3040"}`, borderRadius: 8,
            color: canExecute ? "#04070f" : "var(--muted)", fontFamily: "var(--hd)", fontSize: ".85rem", fontWeight: 800, letterSpacing: ".1em", cursor: canExecute ? "pointer" : "not-allowed", opacity: busy ? .6 : 1 }}>
          {busy ? "QUEUEING…" : canExecute ? "⚡ EXECUTE LIVE TRIGGER" : "⛔ BLOCKED"}
        </button>
      )}
      <div style={{ fontSize: ".62rem", color: "var(--muted)", fontFamily: "var(--bd)", lineHeight: 1.5 }}>
        Routes to <b>your</b> FlexxAxelBridge queue. Requires MT5 connected in Profile. Invalid order/price combinations are blocked before they reach MT5.
      </div>
    </div>
  );
}

export function LiveTriggerPanel({ asset }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [ctx, setCtx] = useState("");
  const [copied, setCopied] = useState(false);
  const hub = useIntelHubOptional();

  // Publish the freshly generated signal to the Intel Hub so the AssetChart
  // Signal Ladder can read it, and auto-switch the chart to this symbol
  // (one-way sync: chart follows the signal). Additive — does not change how
  // the signal is generated or stored.
  useEffect(() => {
    if (!hub || !data || data.is_wait) return;
    const map = LT_ORDER_MAP[data.signal_type];
    if (!map) return;
    hub.setActiveSignal({
      symbol: asset,
      direction: map.direction,
      entry: map.market ? null : (data.entry?.price ?? null),
      stop_loss: data.stop_loss ?? null,
      tp1: data.targets?.tp1 ?? null,
      tp2: data.targets?.tp2 ?? null,
      tp3: data.targets?.tp3 ?? null,
      source: "live_trigger",
    });
    hub.setChartSymbol(asset);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, asset]);

  if (!LT_SUPPORTED.includes(asset)) {
    return (
      <div className="fu" data-testid="lt-unsupported" style={{ padding: "32px", textAlign: "center", border: "1px dashed var(--border)", borderRadius: 8 }}>
        <div style={{ fontSize: "1.4rem", marginBottom: 8 }}>⚡</div>
        <div style={{ fontFamily: "var(--hd)", fontSize: ".95rem", color: "var(--gold)", letterSpacing: ".06em", marginBottom: 6 }}>LIVE TRIGGER — {asset} COMING SOON</div>
        <div style={{ fontSize: ".75rem", color: "var(--muted)", fontFamily: "var(--bd)", lineHeight: 1.7 }}>
          AXEL Live Trigger is live for <b style={{ color: "var(--text)" }}>XAUUSD</b> and <b style={{ color: "var(--text)" }}>BTCUSD</b>.<br />For {asset}, use <b style={{ color: "var(--text)" }}>MT5 Setup</b> or <b style={{ color: "var(--text)" }}>Session Bias</b> for now.
        </div>
      </div>
    );
  }

  async function run() {
    if (loading) return;
    setLoading(true); setErr(""); setData(null);
    try {
      const d = await fetchLiveTrigger(asset, ctx.trim());
      setData(d);
    } catch (e) {
      setErr(e.message || "Connection error. Please retry.");
    }
    setLoading(false);
  }

  function copyExport() {
    if (!data?.export_block) return;
    navigator.clipboard?.writeText(data.export_block);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  }

  if (loading) {
    return (
      <div className="fu" style={{ padding: "42px 20px", textAlign: "center" }}>
        <div style={{ marginBottom: 18 }}><Spinner /></div>
        <div style={{ fontFamily: "var(--hd)", fontSize: "1rem", fontWeight: 700, color: "var(--white)", letterSpacing: ".08em", marginBottom: 8 }}>READING LIVE {asset} MARKET...</div>
        <div style={{ fontSize: ".73rem", color: "var(--muted)", fontFamily: "var(--bd)", lineHeight: 1.9 }}>
          Snapping live price · checking events · scanning news & sentiment · building trigger
        </div>
      </div>
    );
  }

  if (data) {
    const sig = SIG_STYLE[data.signal_type] || SIG_STYLE["WAIT"];
    const conf = data.confidence || 0;
    const confCol = conf > 70 ? "var(--green)" : conf > 40 ? "var(--gold)" : "var(--red)";
    return (
      <div className="fu" data-testid="live-trigger-result">
        {/* Header: price + timestamp + signal type */}
        <div style={{ background: "var(--surf)", border: "1px solid var(--border)", borderRadius: 10, padding: "16px 15px", marginBottom: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 10 }}>
            <div>
              <div style={{ fontFamily: "var(--hd)", fontSize: ".62rem", letterSpacing: ".14em", textTransform: "uppercase", color: "var(--muted)" }}>Live Price · {data.symbol}</div>
              <div data-testid="lt-current-price" style={{ fontFamily: "var(--mono)", fontSize: "1.8rem", fontWeight: 600, color: "var(--white)", lineHeight: 1.1, marginTop: 2 }}>
                {fmtNum(data.current_price)}
              </div>
              <div style={{ fontSize: ".62rem", color: "var(--muted)", fontFamily: "var(--bd)", marginTop: 3 }}>
                {data.timestamp_label} {data.price_source ? `· ${data.price_source}` : ""}
              </div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div data-testid="lt-signal-type" style={{ display: "inline-flex", alignItems: "center", gap: 6, background: `${sig.c}18`, border: `1.5px solid ${sig.c}`, color: sig.c, padding: "6px 12px", borderRadius: 7, fontFamily: "var(--hd)", fontWeight: 800, fontSize: ".92rem", letterSpacing: ".06em" }}>
                <span>{sig.icon}</span><span>{sig.label}</span>
              </div>
              <div style={{ fontSize: ".66rem", color: "var(--muted)", fontFamily: "var(--bd)", marginTop: 5 }}>
                Bias: <b style={{ color: "var(--text)" }}>{data.bias}</b> · {data.preferred_session}
              </div>
            </div>
          </div>
          {/* Confidence */}
          <div style={{ marginTop: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, fontSize: ".62rem", color: "var(--muted)" }}>
              <span>AXEL CONFIDENCE</span><span style={{ color: confCol }}>{conf}%</span>
            </div>
            <div style={{ height: 4, background: "var(--border)", borderRadius: 2 }}>
              <div style={{ height: "100%", borderRadius: 2, width: `${conf}%`, background: confCol, transition: "width 1s ease" }} />
            </div>
          </div>
        </div>

        {/* WAIT banner */}
        {data.is_wait && (
          <div data-testid="lt-wait-banner" style={{ background: "#1a1200", border: `1px solid ${sig.c}55`, borderLeft: `3px solid ${sig.c}`, borderRadius: 9, padding: "14px 15px", marginBottom: 12 }}>
            <div style={{ fontFamily: "var(--hd)", fontSize: ".95rem", fontWeight: 800, color: sig.c, letterSpacing: ".06em", marginBottom: 6 }}>✋ WAIT — NO LIVE TRIGGER</div>
            <div style={{ fontSize: ".82rem", color: "var(--text)", fontFamily: "var(--bd)", lineHeight: 1.7 }}>{data.why_now}</div>
            {data.technical_trigger && (
              <div style={{ marginTop: 8, fontSize: ".76rem", color: "var(--muted)", fontFamily: "var(--bd)", lineHeight: 1.6 }}>
                <b style={{ color: "var(--text)" }}>To activate:</b> {data.technical_trigger}
              </div>
            )}
          </div>
        )}

        {/* Trade params (hidden on WAIT) */}
        {!data.is_wait && (
          <div data-testid="lt-params" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(80px, 1fr))", gap: 6, marginBottom: 12 }}>
            {[
              { l: "ENTRY", v: data.entry?.price, c: "#F5A623" },
              { l: "STOP", v: data.stop_loss, c: "#ff4757" },
              { l: "TP1", v: data.targets?.tp1, c: "#00d68f" },
              { l: "TP2", v: data.targets?.tp2, c: "#00d68f" },
              { l: "TP3", v: data.targets?.tp3, c: "#00d68f" },
            ].map((x) => (
              <div key={x.l} style={{ background: "#020608", border: "1px solid var(--border)", borderTop: `2px solid ${x.c}`, borderRadius: 7, padding: "9px 6px", textAlign: "center" }}>
                <div style={{ fontSize: ".58rem", color: "var(--muted)", fontFamily: "var(--hd)", letterSpacing: ".1em" }}>{x.l}</div>
                <div style={{ fontFamily: "var(--mono)", fontSize: ".82rem", color: x.c, fontWeight: 600, marginTop: 3 }}>{fmtNum(x.v)}</div>
              </div>
            ))}
          </div>
        )}

        {/* Why now */}
        {!data.is_wait && data.why_now && (
          <div style={{ background: "var(--gold)0d", border: "1px solid #2a1a00", borderLeft: "3px solid var(--gold)", borderRadius: 8, padding: "12px 14px", marginBottom: 12 }}>
            <div style={{ fontFamily: "var(--hd)", fontSize: ".62rem", letterSpacing: ".12em", textTransform: "uppercase", color: "var(--gold)", marginBottom: 5 }}>⚡ Why this is a live trigger now</div>
            <div style={{ fontSize: ".82rem", color: "var(--white)", fontFamily: "var(--bd)", lineHeight: 1.7 }}>{data.why_now}</div>
          </div>
        )}

        {/* EXECUTE LIVE TRIGGER — MT5 bridge gate (hidden on WAIT / unsupported assets) */}
        {!data.is_wait && LT_ORDER_MAP[data.signal_type] && MT5_BRIDGE_ASSETS.includes(asset) && (
          <LiveTriggerExecution data={data} asset={asset} />
        )}

        {/* Detail cards */}
        <div style={{ display: "grid", gap: 8, marginBottom: 12 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 8 }}>
            {data.validity && <InfoCard testid="lt-validity" title="Validity" color="#38bdf8">{data.validity}</InfoCard>}
            {data.invalidation && <InfoCard testid="lt-invalidation" title="Invalidation" color="#ff4757">{data.invalidation}</InfoCard>}
          </div>
          <InfoCard testid="lt-session" title={`Preferred Session · ${data.preferred_session}`} color="#c084fc">{data.session_note}</InfoCard>
          <InfoCard testid="lt-event-risk" title={`Event Risk · ${data.event_risk}`} color={RISK_COLOR[data.event_risk] || "#F5A623"}>{data.event_note}</InfoCard>
          {data.technical_trigger && !data.is_wait && <InfoCard testid="lt-technical" title="Technical Trigger" color="#4ECDC4">{data.technical_trigger}</InfoCard>}
          {(data.sentiment_driver || data.narrative_bias || data.catalyst_risk) && (
            <InfoCard testid="lt-sentiment" title="Sentiment & Drivers" color="#F97316">
              {data.sentiment_driver && <div>{data.sentiment_driver}</div>}
              {data.narrative_bias && <div style={{ marginTop: 4 }}><b style={{ color: "var(--text)" }}>Narrative:</b> {data.narrative_bias}</div>}
              {data.catalyst_risk && <div style={{ marginTop: 4 }}><b style={{ color: "var(--text)" }}>Catalyst risk:</b> {data.catalyst_risk}</div>}
            </InfoCard>
          )}
          <InfoCard testid="lt-quant" title="Quant Overlay (optional)" color={data.quant_overlay?.available ? "#34D399" : "var(--muted)"}>{data.quant_overlay?.note}</InfoCard>
          {(data.no_trade_condition || data.do_not_take_after) && (
            <InfoCard testid="lt-guards" title="Trade Guards" color="#fb7185">
              {data.no_trade_condition && <div>⛔ {data.no_trade_condition}</div>}
              {data.do_not_take_after && <div style={{ marginTop: 4 }}>⏱ {data.do_not_take_after}</div>}
            </InfoCard>
          )}
        </div>

        {/* Actions */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
          <button data-testid="lt-copy-btn" onClick={copyExport}
            style={{ flex: 1, minWidth: 140, padding: "11px 8px", background: "transparent", border: "1.5px solid #2AABEE", borderRadius: 8, color: "#2AABEE", fontFamily: "var(--hd)", fontSize: ".8rem", fontWeight: 700, letterSpacing: ".06em", cursor: "pointer" }}>
            {copied ? "✓ COPIED" : "📋 COPY / EXPORT BLOCK"}
          </button>
          <button data-testid="lt-new-btn" onClick={() => setData(null)}
            style={{ flex: 1, minWidth: 140, padding: "11px 8px", background: "transparent", border: "1px solid var(--border)", borderRadius: 8, color: "var(--muted)", fontFamily: "var(--hd)", fontSize: ".8rem", fontWeight: 700, letterSpacing: ".06em", cursor: "pointer" }}>
            🔄 NEW TRIGGER
          </button>
        </div>

        <div style={{ padding: "10px 12px", background: "#050710", border: "1px solid #1a2030", borderRadius: 8, textAlign: "center", fontSize: ".58rem", color: "#2a3545", letterSpacing: ".08em", textTransform: "uppercase", lineHeight: 1.9, fontFamily: "var(--hd)" }}>
          {data.axel_note}<br />For educational purposes only. Not financial advice. Manage your own risk.
        </div>
      </div>
    );
  }

  // Idle / form state
  return (
    <div className="fu">
      {err && <div style={{ padding: "11px 13px", background: "#1a0810", border: "1px solid #5a1030", borderRadius: 7, marginBottom: 13, color: "#ff6b88", fontSize: ".8rem" }}>{err}</div>}
      <div style={{ padding: "13px 15px", background: "rgba(245,166,35,0.08)", border: "1px solid rgba(245,166,35,0.3)", borderRadius: 8, marginBottom: 16 }}>
        <div style={{ fontFamily: "var(--hd)", fontSize: ".95rem", fontWeight: 800, color: "var(--white)", marginBottom: 4, letterSpacing: ".03em" }}>⚡ AXEL Live Trigger — {asset}</div>
        <div style={{ fontSize: ".76rem", color: "var(--muted)", fontFamily: "var(--bd)", lineHeight: 1.6 }}>
          A current-price-sensitive trade idea anchored to the EXACT live price right now. Decides between MARKET / STOP / LIMIT / WAIT based on how close price is to a real trigger — plus session, event-risk, news & sentiment. Best for active execution (MT5 Setup remains for swing planning).
        </div>
      </div>
      <div style={{ marginBottom: 6, fontSize: ".63rem", letterSpacing: ".14em", color: "var(--muted)", textTransform: "uppercase" }}>Trader context <span style={{ color: "#2a3040" }}>(optional)</span></div>
      <textarea value={ctx} onChange={(e) => setCtx(e.target.value)} placeholder={`e.g. already long ${asset} from earlier, looking to add...`} rows={2}
        data-testid="lt-context-input"
        style={{ width: "100%", padding: "10px 13px", marginBottom: 16, background: "var(--surf)", border: "1.5px solid var(--border)", borderRadius: 7, color: "var(--text)", fontFamily: "var(--bd)", fontSize: ".83rem", resize: "none", lineHeight: 1.65 }} />
      <button className="runbtn" data-testid="lt-run-btn" onClick={run}
        style={{ width: "100%", padding: "14px", background: "transparent", border: "1.5px solid #F5A623", borderRadius: 7, color: "#F5A623", fontSize: ".95rem", fontWeight: 700, letterSpacing: ".12em", textTransform: "uppercase" }}>
        ⚡ GENERATE {asset} LIVE TRIGGER
      </button>
      <div style={{ marginTop: 8, textAlign: "center", fontSize: ".62rem", color: "var(--muted)", fontFamily: "var(--bd)" }}>AXEL snaps the live price + scans news/events before generating</div>
    </div>
  );
}

export default LiveTriggerPanel;
